// CreateAccountScreen.js
import { ArrowRightAlt } from "@mui/icons-material";
import { Alert, Button, Divider, Link } from "@mui/material";
import { grey } from "@mui/material/colors";
import { useStytch, useStytchSession } from "@stytch/react";
import { isFunction } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useFormState } from "react-final-form";
import { useNavigate } from "react-router-dom";
import DashboardLogo from "../assets/DashboardLogo";
import AnimatedCheckmark from "../components/feedback/AnimatedCheckmark/AnimatedCheckMark";
import RffForm from "../components/finalForm/RffForm";
import RffEmailField from "../components/finalForm/inputs/RffEmailField";
import RffPhoneField from "../components/finalForm/inputs/RffPhoneField";
import RffTextField from "../components/finalForm/inputs/RffTextField";
import RffGroup from "../components/finalForm/shared/RffGroup";
import Flx from "../components/layout/Flx";
import Htag from "../components/typography/Htag";
import Txt from "../components/typography/Txt";
import { useBorrowerPortalHook } from "../hooks/useBorrowerPortalHook";
import AuthLayout, { AuthGlassCard } from "../components/layout/AuthLayout";

const CreateAccountScreen = ({ partner = "handsome-homebuyer" }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { session } = useStytchSession();
  const { signUpBorrower } = useBorrowerPortalHook();
  const stytch = useStytch();

  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const onSubmit = async (data) => {
    const { emailAddress } = data;
    if (!emailAddress) {
      setError("Please enter an email address");
      return;
    }

    setLoading(true);
    signUpBorrower({
      data: data,
      onSuccessFn: () => {
        sendMagicLinkEmail(data?.emailAddress);
      },
      onFailFn: () => {
        setLoading(false);
        setError("Failed to create account. Please try again.");
      },
    });
    setError("");
  };

  const sendMagicLinkEmail = async (email) => {
    await stytch.magicLinks.email
      .send(email, {
        login_magic_link_url: `${window.location.origin}/authenticate`,
        login_expiration_minutes: 10080,
        session_duration_minutes: 10080,
      })
      .then(() => {
        setSuccess(true);
        setLoading(false);
      })
      .catch((err) => {
        setError(
          "Failed to send magic link. Please go to the login page and try again.",
        );
        setLoading(false);
      });
  };

  // Redirect if already logged in
  useEffect(() => {
    if (session) {
      navigate("/");
    }
  }, [session, navigate]);

  if (success) {
    return (
      <AuthGlassCard>
        <Flx center column gap={2} sx={{ mb: 4 }}>
          <DashboardLogo width="145px" />
          <Htag sx={{ mt: 2 }} h1>
            Borrower Account Created!
          </Htag>
          <Txt center sx={{ mb: 2, px: 1 }}>
            A magic link has been sent to your email address. Please check your
            inbox to complete the account creation process and log in.
          </Txt>
        </Flx>
        <AnimatedCheckmark />
        <Flx gap={2} center sx={{ mt: 4 }}>
          <Flx center g={1}>
            <OutlookSvg />
            <Link
              underline="none"
              href="https://outlook.office.com/mail/0/inbox"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                fontFamily: "var(--ibm)",
                color: "#2962ff",
                fontWeight: 400,
                fontSize: "13px",
              }}
            >
              Open in Outlook
            </Link>
          </Flx>
          <Divider
            orientation="vertical"
            flexItem
            sx={{ borderColor: grey[400] }}
          />
          <Link
            underline="none"
            onClick={() => navigate("/login")}
            role="button"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontFamily: "var(--ibm)",
              color: "#2962ff",
              fontWeight: 400,
              fontSize: "13px",
              cursor: "pointer",
            }}
          >
            Go to Login Page
            <ArrowRightAlt />
          </Link>
        </Flx>
      </AuthGlassCard>
    );
  }

  return (
    <AuthLayout
      title={"Create Borrower Account"}
      description="To create an account, please fill in your details below."
      cardWidth={480}
    >
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, width: "100%" }}
          onClose={() => setError("")}
        >
          {error}
        </Alert>
      )}

      <RffForm
        onSubmit={onSubmit}
        progressVariant="linear"
        submitText="Sign Up"
        suppressFooter
        initialValues={{}}
        sx={{ width: "100%", ".MuiButtonBase-root": { width: "100%" } }}
      >
        <RffGroup
          columnSpacing={2}
          rowSpacing={3.4}
          suppressBottomMargin
          sx={{ mb: 3 }}
          stepName="Account Details"
        >
          <RffTextField required name="firstName" label="First Name" size={6} />
          <RffTextField required name="lastName" label="Last Name" size={6} />
          <RffEmailField required name="emailAddress" label="Email Address" />
          <RffPhoneField required name="phone" label="Phone Number" />
        </RffGroup>
        <RffSubmitButton loading={loading} sx={{ mt: 2 }}>
          Create Account
        </RffSubmitButton>
      </RffForm>

      <Flx center sx={{ mt: 3 }}>
        <Txt center>
          Already have an account?{" "}
          <Link
            color="primary"
            onClick={() => navigate("/login")}
            sx={{
              textTransform: "none",
              ml: 0.5,
              textDecoration: "none",
              cursor: "pointer",
            }}
          >
            Sign In
          </Link>
        </Txt>
      </Flx>
    </AuthLayout>
  );
};

// ─── Submit Button ──────────────────────────────────────────────────────────
const RffSubmitButton = ({
  onSubmit,
  size = "large",
  variant = "contained",
  fullWidth = true,
  sx,
  children,
  loading,
}) => {
  const { values } = useFormState();
  return (
    <Button
      type={isFunction(onSubmit) ? "button" : "submit"}
      onClick={() => {
        if (isFunction(onSubmit)) {
          onSubmit(values);
        }
      }}
      variant={variant}
      size={size}
      loading={loading}
      fullWidth={fullWidth}
      sx={sx}
    >
      {children}
    </Button>
  );
};

// ─── Outlook SVG ────────────────────────────────────────────────────────────
const OutlookSvg = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      version="1.1"
      style={{ width: "16px" }}
      viewBox="0 0 103.17322 104.31332"
      xmlSpace="preserve"
    >
      <defs id="defs43"></defs>
      <path
        d="m 64.566509,22.116383 v 20.404273 l 7.130526,4.489881 c 0.188058,0.05485 0.595516,0.05877 0.783574,0 L 103.16929,26.320259 c 0,-2.44867 -2.28412,-4.203876 -3.573094,-4.203876 H 64.566509 z"
        style={{ fill: "rgb(0, 114, 198)" }}
      />
      <path
        d="m 64.566509,50.13308 6.507584,4.470291 c 0.916782,0.673874 2.021622,0 2.021622,0 -1.100922,0.673874 30.077495,-20.035993 30.077495,-20.035993 v 37.501863 c 0,4.082422 -2.61322,5.794531 -5.551621,5.794531 H 64.562591 V 50.13308 z"
        style={{ fill: "rgb(0, 114, 198)" }}
      />
      <g transform="matrix(3.9178712,0,0,3.9178712,-13.481403,-41.384473)">
        <path
          d="m 11.321,20.958 c -0.566,0 -1.017,0.266 -1.35,0.797 -0.333,0.531 -0.5,1.234 -0.5,2.109 0,0.888 0.167,1.59 0.5,2.106 0.333,0.517 0.77,0.774 1.31,0.774 0.557,0 0.999,-0.251 1.325,-0.753 0.326,-0.502 0.49,-1.199 0.49,-2.09 0,-0.929 -0.158,-1.652 -0.475,-2.169 -0.317,-0.516 -0.75,-0.774 -1.3,-0.774 z"
          style={{ fill: "rgb(0, 114, 198)" }}
        />
        <path
          d="m 3.441,13.563 v 20.375 l 15.5,3.25 V 10.563 l -15.5,3 z m 10.372,13.632 c -0.655,0.862 -1.509,1.294 -2.563,1.294 -1.027,0 -1.863,-0.418 -2.51,-1.253 C 8.094,26.4 7.77,25.312 7.77,23.97 c 0,-1.417 0.328,-2.563 0.985,-3.438 0.657,-0.875 1.527,-1.313 2.61,-1.313 1.023,0 1.851,0.418 2.482,1.256 0.632,0.838 0.948,1.942 0.948,3.313 10e-4,1.409 -0.327,2.545 -0.982,3.407 z"
          style={{ fill: "rgb(0, 114, 198)" }}
        />
      </g>
    </svg>
  );
};

export default CreateAccountScreen;
